/**
 * Apply-time nudge for a weak resume — Recruiter Check BR-7.
 *
 * ONE helper wraps every path that moves a card to Applied (Kanban drag-drop,
 * the JobCard "Applied!" button, the Move-to menu, the Kanban quick-Apply
 * action) so the confirm can't be bypassed by taking a different route. The
 * previous design — a check inside KanbanBoard — left three ways around it.
 *
 * It is a nudge, never a wall (design principle 5):
 *   • only fires moving TO 'Applied'
 *   • only when the card's stored grade is C or D
 *   • only until the user says "Apply anyway" once (`recruiter_ack === '1'`,
 *     written server-side by the same phase update)
 * A card with no grade (every resume rendered before Recruiter Check) adds
 * exactly zero friction.
 *
 * Usage — the caller AWAITS the decision before doing anything optimistic:
 *
 *   const { guardPhaseMove, applyGateDialog } = useApplyGate();
 *   const decision = await guardPhaseMove(job, 'Applied', { onFixFirst });
 *   if (!decision.proceed) return;                     // user backed out
 *   await api.updateJobPhase(job.jobid, 'Applied', decision.recruiterAck);
 *   ...
 *   return <>{applyGateDialog}</>;
 */
import { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle } from 'lucide-react';
import { useEscapeClose } from './useEscapeClose';
import { parseOutputQuality, isRiskyGrade, gradeWord } from '../utils/outputQuality';
import type { JobPhase, RecruiterGrade, UserJob } from '../types';

/** What the caller should do next. `recruiterAck` = remember "Apply anyway". */
export interface ApplyGateDecision {
  proceed: boolean;
  recruiterAck: boolean;
}

const PROCEED_CLEAN: ApplyGateDecision = { proceed: true, recruiterAck: false };

/**
 * Does this move need the confirm? Pure, so the rule is testable and identical
 * everywhere. Exported for tests and for callers that want to pre-check.
 */
export function needsApplyConfirm(job: UserJob, phase: JobPhase): RecruiterGrade | null {
  if (phase !== 'Applied') return null;
  if (job.recruiter_ack === '1') return null;
  const grade = parseOutputQuality(job.output_quality)?.grade;
  return isRiskyGrade(grade) ? grade! : null;
}

interface PendingConfirm {
  grade: RecruiterGrade;
  jobcompany?: string;
  jobtitle?: string;
  onFixFirst?: () => void;
}

export function useApplyGate() {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  // The promise the caller is awaiting. Settled EXACTLY once, by whichever
  // button (or Esc / backdrop) resolves the dialog.
  const resolveRef = useRef<((d: ApplyGateDecision) => void) | null>(null);

  const settle = useCallback((decision: ApplyGateDecision) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    setPending(null);
    resolve?.(decision);
  }, []);

  /**
   * Ask, if the move warrants asking. Resolves immediately (proceed) for every
   * move that doesn't — so callers can await it unconditionally.
   */
  const guardPhaseMove = useCallback(
    (job: UserJob, phase: JobPhase, opts?: { onFixFirst?: () => void }): Promise<ApplyGateDecision> => {
      const grade = needsApplyConfirm(job, phase);
      if (!grade) return Promise.resolve(PROCEED_CLEAN);
      // A second request while one is open cancels the first rather than
      // orphaning its promise (the caller would hang forever otherwise).
      resolveRef.current?.({ proceed: false, recruiterAck: false });
      return new Promise<ApplyGateDecision>((resolve) => {
        resolveRef.current = resolve;
        setPending({
          grade,
          jobcompany: job.jobcompany,
          jobtitle: job.jobtitle,
          onFixFirst: opts?.onFixFirst,
        });
      });
    },
    [],
  );

  const applyGateDialog = pending
    ? <ApplyGateDialog pending={pending} onSettle={settle} />
    : null;

  return { guardPhaseMove, applyGateDialog };
}

function ApplyGateDialog({
  pending,
  onSettle,
}: {
  pending: PendingConfirm;
  onSettle: (d: ApplyGateDecision) => void;
}) {
  const cancel = useCallback(
    () => onSettle({ proceed: false, recruiterAck: false }),
    [onSettle],
  );
  useEscapeClose(cancel, true);

  const subject = [pending.jobcompany, pending.jobtitle].filter(Boolean).join(' — ');

  // PORTALED for the same reason QualityModal is: a `fixed inset-0` overlay
  // rendered inside a job card positions against the card, not the viewport.
  return createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80] p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Applying with a weak resume"
      data-testid="apply-gate-confirm"
      data-grade={pending.grade}
      onClick={cancel}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 flex items-start gap-3">
          <span className="flex-shrink-0 w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
          </span>
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900">
              This resume grades {pending.grade} — {gradeWord(pending.grade).toLowerCase()}
            </h2>
            {subject && <p className="text-xs text-gray-500 mt-0.5 truncate">{subject}</p>}
            <p className="text-sm text-gray-700 mt-2 leading-relaxed">
              Recruiters are likely to pass on it. Takes about 10 minutes to fix — Recruiter
              Check names exactly what to change and where.
            </p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t bg-gray-50 rounded-b-lg">
          <button
            type="button"
            onClick={() => {
              const fix = pending.onFixFirst;
              onSettle({ proceed: false, recruiterAck: false });
              fix?.();
            }}
            data-testid="apply-gate-fix"
            className="px-3 py-2 text-sm font-medium text-primary-700 border border-primary-300 rounded-lg hover:bg-primary-50"
          >
            Fix it first
          </button>
          <button
            type="button"
            onClick={() => onSettle({ proceed: true, recruiterAck: true })}
            data-testid="apply-gate-proceed"
            className="px-3 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
          >
            Apply anyway
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default useApplyGate;
